import React, { Fragment, useCallback, useEffect, useState } from 'react';
import { AxiosResponse } from 'axios';
import {
  Autocomplete,
  CircularProgress,
  TextField,
  debounce,
  Typography,
  colors,
} from '@mui/material';
import { Search } from '@mui/icons-material';

import { IGetSearch } from 'apis/type';
import useClassSection from 'store/section/useClassSection';
import usePatient from 'store/patient/usePatient';

interface Props {
  listKey: string;
  valueKey: string;
  variant?: 'outlined' | 'standard';
  noOptionsText?: string;
  helperText?: string;
  placeholder?: string;
  onChange: (value: any) => void;
  getOptionLabel: (option: any) => string;
  renderOption?: (props: any, option: any) => React.ReactNode;
  getApi: (request: IGetSearch) => Promise<AxiosResponse<any>>;
  isDisabled?: boolean;
  options?: any[];
  syncWithPatient?: boolean;
}

const MuiAutocomplete = (props: Props) => {
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [options, setOptions] = useState<any[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedValue, setSelectedValue] = useState<any>(null);
  const { class_section_id } = useClassSection();
  const { patient } = usePatient();
  const {
    listKey,
    valueKey,
    variant = 'outlined',
    noOptionsText,
    helperText,
    placeholder,
    onChange,
    renderOption,
    getOptionLabel,
    getApi,
    isDisabled,
    syncWithPatient = false,
  } = props;

  const onChangeOptions = useCallback(
    (keyword: string) => {
      setIsLoading(true);
      getApi({ page: 1, keyword })
        .then(({ data }) => {
          setTotalCount(data?.count || 0);
          setOptions(data?.[listKey] || []);
        })
        .catch(error => {
          console.error('Failed to fetch options:', error);
          setOptions([]);
          setTotalCount(0);
        })
        .finally(() => setIsLoading(false));
    },
    // eslint-disable-next-line
    [class_section_id, patient, listKey]
  );

  useEffect(() => {
    if (open) {
      onChangeOptions('');
    }
  }, [onChangeOptions, open]);

  useEffect(() => {
    if (!syncWithPatient) return;
    setSelectedValue(patient ?? null);
  }, [patient, syncWithPatient]);

  const isOptionEqualToValue = (option: any, value: any) => {
    if (!option || !value) return false;
    return option[valueKey] === value[valueKey];
  };

  const onChangeTextField = debounce(e => {
    const keyword = e?.target?.value || '';
    onChangeOptions(keyword);
  }, 500);

  const handleChange = (_: any, value: any) => {
    setSelectedValue(value);
    onChange(value);

    // X 버튼 클릭 시 (value가 null) 옵션 초기화
    if (value === null) {
      onChangeOptions('');
    }
  };

  const TextFieldEndAdornment = ({
    endAdornment,
  }: {
    endAdornment: React.ReactNode;
  }) => (
    <Fragment>
      {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
      {endAdornment}
    </Fragment>
  );

  return (
    <Autocomplete
      fullWidth
      open={open}
      value={selectedValue}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      isOptionEqualToValue={isOptionEqualToValue}
      renderOption={renderOption}
      getOptionLabel={getOptionLabel}
      noOptionsText={<Typography variant="caption">{noOptionsText}</Typography>}
      options={options}
      loading={isLoading}
      disabled={isDisabled}
      onChange={handleChange}
      renderInput={params => (
        <TextField
          {...params}
          variant={variant}
          helperText={helperText}
          placeholder={placeholder}
          onChange={onChangeTextField}
          InputProps={{
            ...params.InputProps,
            startAdornment: totalCount ? (
              <Typography
                variant="caption"
                color={colors.grey[500]}
                sx={{ mr: 1 }}
              >
                (Total {totalCount})
              </Typography>
            ) : (
              <Search color="disabled" sx={{ fontSize: 16, mr: 1 }} />
            ),
            endAdornment: (
              <TextFieldEndAdornment
                endAdornment={params.InputProps.endAdornment}
              />
            ),
            sx: { fontSize: 14 },
          }}
        />
      )}
    />
  );
};

export default MuiAutocomplete;
